import { useEffect } from 'react';
import type { IAhuBoardProject, IAhuBoardUnit, IAllPermissions, IBoardUser } from 'models';

const AHU_CLASSES = [
  'ahu-access-permission',
  'ahu-view-permission',
  'ahu-no_access-permission',
] as const;

const MODEL_VIEW_CLASSES = [
  'model_view-access-permission',
  'model_view-view-permission',
  'model_view-no_access-permission',
] as const;

export type AhuPermissionLevel = 'access' | 'view' | 'no_access';

/**
 * Whether `user` may create/edit hybrid AHUs. Falls back to 'standard' when
 * neither `effective_user_skill` nor `user_skill` is present (matches the
 * backend's own null-safe default — see `KsdmUser.effective_user_skill`).
 * A standard user viewing a hybrid AHU is locked to a read-only board
 * (separate from, and in addition to, the `ahu`/`model_view` gate below).
 */
export function isPowerUser(user: IBoardUser | undefined): boolean {
  const skill = user?.effective_user_skill ?? user?.user_skill ?? 'standard';
  return skill === 'power';
}

/**
 * Pure derivation of the MainView permission pipeline (§2.4 render +
 * _applyAhuPermissions): `ahu_board_items` rows 'ahu'/'model_view' map
 * access→'access' else 'view'; the AHU permission downgrades to 'view' when
 * `project.is_critical_task_started && user.access_type === 'project'`, when
 * `!project.editable && user.access_type === 'network'` (network read-only),
 * or — hybrid AHU (KA-x, main.js `_isHybridStandardLock`) — when `ahu.is_hybrid`
 * and the user isn't a Power user: the whole board goes view-only, same as
 * every other downgrade here (reuses the existing CSS gate rather than a
 * separate mechanism).
 *
 * `hybridStandardLock` reports the last of those as its own flag: true only
 * when the hybrid gate is the SOLE reason the board is view-only (the user
 * would otherwise have 'access'). The Drawing window's Settings / Callout
 * Schedule blocks stay editable in that case — they drive Model View output,
 * not the AHU configuration the hybrid gate protects — while a genuinely
 * read-only board (no `ahu` write permission, network read-only, critical
 * task started) keeps them locked like everything else.
 */
export function deriveAhuPermissions(
  permissions: IAllPermissions | undefined,
  project: IAhuBoardProject | undefined,
  user: IBoardUser | undefined,
  ahu?: IAhuBoardUnit
): {
  ahu: AhuPermissionLevel;
  modelView: AhuPermissionLevel;
  hybridStandardLock: boolean;
  writeAllowed: boolean;
} | null {
  const items = permissions?.permissions?.ahu_board_items;
  if (!items) return null;

  let ahuPerm: AhuPermissionLevel = 'no_access';
  let modelView: AhuPermissionLevel = 'no_access';
  for (const row of items) {
    if (row.item === 'ahu') ahuPerm = row.access ? 'access' : 'view';
    else if (row.item === 'model_view') modelView = row.access ? 'access' : 'view';
  }

  const accessType = user?.access_type;
  const readonly = project?.editable === false && accessType === 'network';
  if (project?.is_critical_task_started && accessType === 'project' && ahuPerm === 'access') {
    ahuPerm = 'view';
  }
  if (readonly) ahuPerm = 'view';
  const hybridStandardLock = !!ahu?.is_hybrid && !isPowerUser(user) && ahuPerm === 'access';
  if (hybridStandardLock) {
    ahuPerm = 'view';
  }

  /**
   * May code WRITE on this board without a user gesture (BoardWriteAccessContext)?
   * Stricter than `ahu === 'access'` on three counts, none of which the CSS
   * gates need and all of which an effect does:
   *  - the downgrades above read `project` and `user`, so 'access' before those
   *    requests land means "not known yet" — and if the project request fails,
   *    never known. An automatic write must wait for them;
   *  - `project.editable` is the server's own per-user answer
   *    (`KsdmProjectSerializer.get_editable` runs `check_access_to_the_project`
   *    with the requesting user), and it is false for an ARCHIVED project —
   *    which the board still opens, and which the `readonly` downgrade above
   *    only catches for a `network` user. The position endpoints never call
   *    that check themselves, so a write from such a board is accepted;
   *  - a static version is a frozen snapshot, read-only on the client through
   *    `.static_version` + `pointer-events: none` ALONE
   *    (`deriveAhuPermissions` has never looked at it, and the server's
   *    `check_access_to_the_ahu` is defined but never called on this path).
   */
  const writeAllowed =
    ahuPerm === 'access' &&
    !!project &&
    project.editable !== false &&
    !!user &&
    !!ahu &&
    !ahu.static_version;

  return { ahu: ahuPerm, modelView, hybridStandardLock, writeAllowed };
}

/**
 * Applies the body permission classes (parity 10/11/14):
 * `ahu-{access|view|no_access}-permission` + `model_view-<perm>-permission`.
 * CSS gates the whole UI off these; the canvas right-click gate (parity 11)
 * reads `ahu-view-permission` in 6d. Recomputes when the project refetches
 * (workflow:done parity 14 — RTKQ invalidation re-runs this hook). Classes are
 * applied only once permissions have loaded, like the legacy render promise.
 * Returns the derivation so callers can read `hybridStandardLock` (the one
 * downgrade that leaves the Drawing window's Model View settings editable).
 */
export function useAhuPermissions(
  permissions: IAllPermissions | undefined,
  project: IAhuBoardProject | undefined,
  user: IBoardUser | undefined,
  ahu?: IAhuBoardUnit
): ReturnType<typeof deriveAhuPermissions> {
  const derived = deriveAhuPermissions(permissions, project, user, ahu);
  const ahuPerm = derived?.ahu;
  const modelViewPerm = derived?.modelView;

  useEffect(() => {
    if (!ahuPerm || !modelViewPerm) return undefined;
    const body = document.body;
    body.classList.remove(...AHU_CLASSES);
    body.classList.add(`ahu-${ahuPerm}-permission`);
    body.classList.remove(...MODEL_VIEW_CLASSES);
    body.classList.add(`model_view-${modelViewPerm}-permission`);
    return () => {
      body.classList.remove(...AHU_CLASSES, ...MODEL_VIEW_CLASSES);
    };
  }, [ahuPerm, modelViewPerm]);

  return derived;
}
